import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Helmet, HelmetProvider } from "react-helmet-async";

import "./App.css";

import ErrorBoundary from './components/ErrorBoundary.js';
import { FirebaseProvider } from './contexts/firebase-context';
import { ManagedModal, ModalProvider } from './contexts/modal-context.js';

import Navbar from "./components/Navbar.js"
import RouteShell from './components/RouteShell.js';
import AppFooter from './components/AppFooter.js';
import InstallPWAPrompt from './components/InstallPWAPrompt.js';
import SWUpdateNotifier from './components/SWUpdateNotifier.js';
import routeMeta from './utils/routeMeta.js';
import { AppThemeModeProvider } from './contexts/theme-mode-context.js';

const NotFound = lazy(() => import("./components/NotFound.js"))
const Unauthorized = lazy(() => import("./components/Unauthorized.js"))
const Home = lazy(() => import("./components/Home.js"))
const AdminPanel = lazy(() => import("./components/AdminPanel.js"))
const Audiometry = lazy(() => import("./components/Audiometry.js"))
const Inventory = lazy(() => import("./components/Inventory.js"))
const GenerateInvoice = lazy(() => import("./components/GenerateInvoice.js"))
const SalesReport = lazy(() => import("./components/SalesReport.js"))
const Reports = lazy(() => import("./components/Reports.js"))
const Patients = lazy(() => import("./components/Patients.js"))
const Service = lazy(() => import("./components/Service.js"))

const getRouteMeta = (path) => routeMeta.find((route) => route.path === path)

function App() {
    return (
        <ErrorBoundary>
            <HelmetProvider>

                <AppThemeModeProvider>
                    <FirebaseProvider>
                        <ModalProvider>
                            <div className="App">
                                <Helmet>
                                    <meta name="description" content="Happy Ears Kolkata is a React-powered app for efficient hearing care management, offering seamless invoice creation, inventory control, and secure patient data storage with integrated location tracking, created by Hritwick De" />
                                    <title>Happy Ears Kolkata Invoicing</title>
                                </Helmet>

                                <BrowserRouter basename="/">
                                    <div className="app-router-shell">
                                        <Navbar />

                                        <main className="app-main-shell">
                                            <Suspense fallback={<RouteShell flat title="Loading"><div /></RouteShell>}>
                                                <Routes>
                                                    <Route path="*" element={<RouteShell flat><NotFound /></RouteShell>} />
                                                    <Route path="/unauthorized" element={<RouteShell flat title="Unauthorized"><Unauthorized /></RouteShell>} />
                                                    <Route path="/" element={<RouteShell flat title="Dashboard" Icon={getRouteMeta('/').Icon}><Home /></RouteShell>} />
                                                    <Route path="/admin-panel" element={<RouteShell title={getRouteMeta('/admin-panel').label} Icon={getRouteMeta('/admin-panel').Icon}><AdminPanel /></RouteShell>} />
                                                    <Route path="/audiometry" element={<RouteShell title={getRouteMeta('/audiometry').label} Icon={getRouteMeta('/audiometry').Icon}><Audiometry /></RouteShell>} />
                                                    <Route path="/inventory" element={<RouteShell title={getRouteMeta('/inventory').label} Icon={getRouteMeta('/inventory').Icon}><Inventory /></RouteShell>} />
                                                    <Route path="/generate-invoice/:audiometryId?" element={<RouteShell title={getRouteMeta('/generate-invoice').label} Icon={getRouteMeta('/generate-invoice').Icon}><GenerateInvoice /></RouteShell>} />
                                                    <Route path="/sales-report" element={<RouteShell title={getRouteMeta('/sales-report').label} Icon={getRouteMeta('/sales-report').Icon}><SalesReport /></RouteShell>} />
                                                    <Route path="/reports" element={<RouteShell title={getRouteMeta('/reports').label} Icon={getRouteMeta('/reports').Icon}><Reports /></RouteShell>} />
                                                    <Route path="/patients" element={<RouteShell title={getRouteMeta('/patients').label} Icon={getRouteMeta('/patients').Icon}><Patients /></RouteShell>} />
                                                    <Route path="/service" element={<RouteShell title={getRouteMeta('/service').label} Icon={getRouteMeta('/service').Icon}><Service /></RouteShell>} />
                                                </Routes>
                                            </Suspense>
                                        </main>

                                        <AppFooter />
                                    </div>

                                    <ManagedModal />
                                    <InstallPWAPrompt />
                                    <SWUpdateNotifier />
                            </BrowserRouter>
                        </div>
                    </ModalProvider>
                </FirebaseProvider>
            </AppThemeModeProvider>

            </HelmetProvider>
        </ErrorBoundary>
    )
}

export default App;
